import { Formik } from 'formik';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { selectConfigurationParameterById } from '!core/redux';
import {
  TrlFormControlLabel,
  TrlPill,
  TrlRadioButton,
} from '»components/atoms';
import { TrlFormButtons } from '»components/molecules/TrlFormButtons';
import { useCurrentPage, useSendOtpBtn } from '»utils/customHooks';
import { isSubmitFormButtonDisabled } from '»utils/form';
import { TrlFormattedMessagePlain } from '»utils/i18n';

import { Otp, otpService } from '../../Otp';
import { CompletedProcessModal } from '../CommonComponents';
import { FormStyled } from '../Onboarding.styles';
import { onboardingSlice } from '../redux';
import {
  onboardingMainNameSelector,
  onboardingOpeningRequestIdSelector,
  userInitialOtpDelivMetdsSelector,
} from '../redux/onboarding.selectors';
import { FormControlStyled } from './ProcessValidation.styles';
import { processValidation } from './ProcessValidation.yup';

export const ProcessValidation = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const onboardingInitialOtpDelivMetds = useSelector(
    userInitialOtpDelivMetdsSelector,
  )?.split(';');
  const onboardingFinalOtpDelivMetds = useSelector((state) =>
    selectConfigurationParameterById(
      state,
      'ONBOARDING_OTP_DELIVERY_METHODS_END',
    ),
  ).value.split(';');
  const openingRequestId = useSelector(onboardingOpeningRequestIdSelector);
  const name = useSelector(onboardingMainNameSelector);

  const { isOnboardingInitialOtpStep } = useCurrentPage(location.pathname);
  const {
    isSendOtpBtnOnHold,
    disableSendOtpButton,
    enableSendOtpButtonWithDelay,
  } = useSendOtpBtn();

  const onboardingOtpDelivMetds = isOnboardingInitialOtpStep
    ? onboardingInitialOtpDelivMetds
    : onboardingFinalOtpDelivMetds;

  const hasOneOtpDelivMetd = onboardingOtpDelivMetds.length === 1;

  const [selectedSentCodeMethod, setSelectedSentCodeMethod] = useState(
    hasOneOtpDelivMetd ? onboardingOtpDelivMetds[0] : '',
  );

  useEffect(() => {
    if (hasOneOtpDelivMetd) {
      disableSendOtpButton();
      enableSendOtpButtonWithDelay();
    }
  }, []);

  const handleSubmit = (values, { setSubmitting }) => {
    const disableFormSubmittingState = () => {
      setSubmitting(false);
    };

    dispatch(
      onboardingSlice.actions.setOnboardingStateRequest({
        openingRequestId,
        state: isOnboardingInitialOtpStep ? 'INITIALOK' : 'ENDOK',
        validationCode: values.validationCode,
        disableFormSubmittingState,
      }),
    );
  };

  const handleChangeSendCodeMethod = (event) => {
    setSelectedSentCodeMethod(event.target.value);
  };

  const sendCode = () => {
    disableSendOtpButton();
    enableSendOtpButtonWithDelay();

    otpService
      .getOTPOnboarding({
        openingRequestId,
        entityName: name,
        otpSendType: selectedSentCodeMethod,
        onboardingState: isOnboardingInitialOtpStep ? 'INITIAL' : 'END',
      })
      .then(() => {})
      .catch((error) => {
        dispatch(onboardingSlice.actions.setOnboardingStateFailure(error));
      });
  };

  return (
    <Formik
      onSubmit={handleSubmit}
      initialValues={{ validationCode: '' }}
      validationSchema={processValidation()}
      enableReinitialize
    >
      {({ isSubmitting, values, dirty, errors }) => (
        <FormStyled className="formContainer">
          <CompletedProcessModal />

          <FormControlStyled>
            {onboardingOtpDelivMetds.map((deliveryMethod) => (
              <TrlFormControlLabel
                key={deliveryMethod}
                id={deliveryMethod}
                label={TrlFormattedMessagePlain({
                  id:
                    deliveryMethod === 'EMAIL'
                      ? 'TRLT_UI-EMAIL'
                      : 'TRLT_UI-SMS',
                })}
                selectedOptionId={selectedSentCodeMethod}
              >
                <TrlRadioButton
                  id={deliveryMethod.toLowerCase()}
                  name={deliveryMethod.toLowerCase()}
                  value={deliveryMethod}
                  selectedValue={selectedSentCodeMethod}
                  onChange={handleChangeSendCodeMethod}
                />
              </TrlFormControlLabel>
            ))}

            <TrlPill
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-REQUEST_CODE',
              })}
              variant="addItem"
              onClick={sendCode}
              disabled={selectedSentCodeMethod === '' || isSendOtpBtnOnHold}
            />
          </FormControlStyled>

          <Otp
            textFieldName="validationCode"
            openingRequestId={openingRequestId}
            currentRoute={location.pathname}
          />

          <TrlFormButtons
            fixedAtBottom
            advanceButtonName="TRLT_UI-NEXT"
            isAdvanceButtonDisabled={isSubmitFormButtonDisabled({
              isSubmitting,
              dirty,
              errors,
              values,
              areFiles: false,
            })}
          />
        </FormStyled>
      )}
    </Formik>
  );
};
